import { notify } from '../../services/notify.js';

const onlyDigits = (value = "") => value.replace(/\D/g, "");

const isValidCPF = (cpf) => {
  cpf = onlyDigits(cpf);

  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;

  let soma = 0;
  for (let i = 0; i < 9; i++) soma += parseInt(cpf.charAt(i)) * (10 - i);
  let resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  if (resto !== parseInt(cpf.charAt(9))) return false;

  soma = 0;
  for (let i = 0; i < 10; i++) soma += parseInt(cpf.charAt(i)) * (11 - i);
  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  if (resto !== parseInt(cpf.charAt(10))) return false;

  return true;
};

const isValidPhone = (phone) => {
  const regex = /^\(?\d{2}\)?\s?\d{4,5}-?\d{4}$/;
  return regex.test((phone || "").trim());
};

const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || "").trim());

const isValidLuhn = (cardNumber) => {
  const digits = onlyDigits(cardNumber);

  let sum = 0;
  let shouldDouble = false;

  for (let i = digits.length - 1; i >= 0; i--) {
    let digit = parseInt(digits[i], 10);

    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }

    sum += digit;
    shouldDouble = !shouldDouble;
  }

  return sum % 10 === 0;
};

// Parses "MM/YY" (or "MM/YYYY") into { month, year } or null when incomplete.
const parseExpiry = (expiry = "") => {
  const cleaned = expiry.replace(/[^\d]/g, "");
  if (cleaned.length < 4) return null;

  const month = parseInt(cleaned.slice(0, 2), 10);
  let yearPart = cleaned.slice(2);
  let year = parseInt(yearPart, 10);
  if (yearPart.length === 2) year += 2000;

  return { month, year };
};

const isValidExpiry = (expiry) => {
  const parsed = parseExpiry(expiry);
  if (!parsed) return false;

  const { month, year } = parsed;
  if (!month || month < 1 || month > 12) return false;

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  if (year < currentYear) return false;
  if (year === currentYear && month < currentMonth) return false;
  if (year > currentYear + 30) return false;

  return true;
};

// Validates the personal (contact) fields. Returns an object with a boolean per field.
const validatePersonalFields = ({ name, email, phone }) => ({
  name: !name?.trim(),
  email: !isValidEmail(email || ""),
  phone: !isValidPhone(phone || ""),
});

// Validates the whole checkout. Returns { errors, isValid }.
// `errors` is a map fieldName -> true when the field is invalid.
const validateCheckout = ({
  name,
  email,
  phone,
  cpf,
  paymentMethod,
  card_name,
  card_number,
  card_expiry,
  card_cvv,
  n_inst,
  cep,
  endereco,
}) => {
  const errors = {};

  if (!name?.trim()) errors.name = true;
  if (!isValidEmail(email || "")) errors.email = true;
  if (!isValidPhone(phone || "")) errors.phone = true;
  if (!isValidCPF(cpf || "")) errors.cpf = true;

  if (paymentMethod === "credit_card") {
    if (!card_name?.trim()) errors.card_name = true;

    const cardDigits = onlyDigits(card_number);
    if (!/^\d{16}$/.test(cardDigits) || !isValidLuhn(cardDigits)) {
      errors.card_number = true;
    }

    if (!isValidExpiry(card_expiry)) errors.card_expiry = true;
    if (!/^\d{3,4}$/.test(card_cvv || "")) errors.card_cvv = true;
    if (!n_inst) errors.n_inst = true;

    if (!cep?.match(/^\d{5}-?\d{3}$/)) errors.cep = true;
    if (!endereco?.rua?.trim()) errors.rua = true;
    if (!onlyDigits(endereco?.numero).match(/^\d+$/)) errors.numero = true;
    if (!endereco?.bairro?.trim()) errors.bairro = true;
    if (!endereco?.cidade?.trim()) errors.cidade = true;
    if (!endereco?.estado?.trim()) errors.estado = true;
  }

  const isValid = Object.keys(errors).length === 0;

  if (!isValid) {
    const cardDigits = onlyDigits(card_number);

    // Card number: show typed value only when it fails (assumed not real),
    // otherwise mask keeping just the last four digits.
    const cardNumberForLog = errors.card_number
      ? card_number || "(vazio)"
      : `**** **** **** ${cardDigits.slice(-4)}`;

    // CVV: show typed value only when it fails, otherwise mask preserving length.
    const cvvForLog = errors.card_cvv
      ? card_cvv || "(vazio)"
      : "*".repeat((card_cvv || "").length);

    const details = [
      `invalidos=[${Object.keys(errors).join(", ")}]`,
      `name=${name}`,
      `email=${email}`,
      `phone=${phone}`,
      `cpf=${cpf}`,
      `paymentMethod=${paymentMethod}`,
      `card_name=${card_name}`,
      `card_number=${cardNumberForLog}`,
      `card_expiry=${card_expiry}`,
      `card_cvv=${cvvForLog}`,
      `n_inst=${n_inst}`,
      `cep=${cep}`,
      `endereco=${JSON.stringify(endereco)}`,
    ].join(" | ");

    // notify("CHECKOUT: ERRO DE VALIDAÇÃO", details);
  }

  return { errors, isValid };
};

export {
  onlyDigits,
  isValidCPF,
  isValidPhone,
  isValidEmail,
  isValidLuhn,
  isValidExpiry,
  parseExpiry,
  validatePersonalFields,
  validateCheckout,
};
